import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { autoUpdate, flip, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions, useListItem, useListNavigation, FloatingList } from '@floating-ui/react';
import breakpoints from '@/config/breakpoints';
import useComposedRefs from '@/hooks/useComposeRefs';
import Button from '@/components/Button';
import { Drawer, DrawerContent } from '@/components/FormElements/internal/Drawer';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var ArrowDownIcon = function ArrowDownIcon(props) {
  return /*#__PURE__*/_jsx("svg", _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M11.7071 0.292986C11.3166 -0.097538 10.6834 -0.097538 10.2929 0.292986L6 4.58588L1.70711 0.292986C1.31658 -0.0975381 0.683417 -0.0975381 0.292893 0.292986C-0.0976314 0.68351 -0.0976314 1.31668 0.292893 1.7072L6 7.41431L11.7071 1.7072C12.0976 1.31668 12.0976 0.68351 11.7071 0.292986Z",
      fill: "#12131A"
    })
  }));
};

ArrowDownIcon.defaultProps = {
  width: "12",
  height: "8",
  viewBox: "0 0 12 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}; // Create a context to avoid prop drilling
// and provide a structure similar to a native select API,
// enabling shared state and logic across the dropdown components.

const SelectContext = /*#__PURE__*/createContext({
  value: '',
  selectedValue: '',
  selectedValueIndex: -1,
  valueOption: null,
  menuOpen: false,
  isMobile: false,
  onMenuOpenChange: () => null,
  alwaysVisible: false,
  onValueSubmit: () => null,
  onValueChange: () => null,
  onOptionAdd: () => null,
  onOptionRemove: () => null,
  onClose: () => null,
  // floating
  // @ts-ignore
  listRef: [],
  floatingStyles: {},
  setReference: () => null,
  setFloating: () => null,
  getReferenceProps: () => ({}),
  getFloatingProps: () => ({}),
  getItemProps: () => ({})
});

const MegaSelect = _ref => {
  let {
    defaultValue = '',
    value: controlledValue,
    children,
    onChange,
    onValueChange,
    alwaysVisible = false
  } = _ref;
  // alwaysVisible is used to keep the menu open
  const {
    0: menuOpen,
    1: setMenuOpen
  } = useState(alwaysVisible);
  const {
    0: localValue,
    1: setValue
  } = useState(defaultValue);
  const {
    0: localSelectedValue,
    1: setSelectedValue
  } = useState(defaultValue);
  const {
    0: optionsSet,
    1: setOptionsSet
  } = useState(new Set()); // controlled value takes precedence

  const value = controlledValue || localValue;
  const selectedValue = controlledValue || localSelectedValue; // Add an option to set on nested option component mount

  const handleOptionAdd = useCallback(option => {
    // set selected value if no value is selected
    if (option.selected || option.disabled) {
      setSelectedValue(prev => prev || option.value);
      setValue(prev => prev || option.value);
    }

    setOptionsSet(prev => new Set(prev).add(option));
  }, []); // remove option from set
  // to make sure there are no duplicates
  // on re-render

  const handleOptionRemove = useCallback(option => {
    setOptionsSet(prev => {
      const optionsSet = new Set(prev);
      optionsSet.delete(option);
      return optionsSet;
    });
  }, []); // find selected option index

  const selectedValueIndex = useMemo(() => {
    return Array.from(optionsSet).findIndex(option => option.value === selectedValue);
  }, [optionsSet, selectedValue]); // find selected option

  const valueOption = useMemo(() => {
    return Array.from(optionsSet).find(option => option.value === value) || null;
  }, [optionsSet, value]);
  const handleValueChange = useCallback(value => {
    setSelectedValue(value);
    typeof onValueChange === 'function' && onValueChange(value);
  }, [onValueChange]);
  const closeMenu = useCallback(() => {
    setMenuOpen(alwaysVisible);
  }, [alwaysVisible]);
  const handleValueSubmit = useCallback(() => {
    // submit value if it has changed
    if (selectedValue && value !== selectedValue) {
      setValue(selectedValue);
      typeof onChange === 'function' && onChange(selectedValue);
    }

    closeMenu();
  }, [onChange, selectedValue, value, closeMenu]);
  const handleMenuOpenChange = useCallback((open, _, reason) => {
    if (!open && reason === 'outside-press') {
      // submit value on outside press
      handleValueSubmit();
    } // reset selected value on open


    if (open) setSelectedValue(value);
    setMenuOpen(open);
  }, [setSelectedValue, value, handleValueSubmit]); // Handle menu positioning

  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    open: menuOpen || alwaysVisible,
    onOpenChange: handleMenuOpenChange,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift()]
  }); // Navigate through options
  // by using arrow keys

  const listRef = useRef([]);
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex: selectedValueIndex,
    loop: true,
    focusItemOnHover: false,
    focusItemOnOpen: false,
    onNavigate: idx => {
      if (idx === null) return;
      const option = Array.from(optionsSet)[idx];
      if (!option) return;
      setSelectedValue(option.value);
    }
  }); // handle keyboard events
  // when menu is open

  useEffect(() => {
    const handleKeyDown = event => {
      // loop through options on tab key
      if (event.key === 'Tab') {
        event.preventDefault();
        setSelectedValue(prev => {
          const options = Array.from(optionsSet);
          const idx = options.findIndex(option => option.value === prev);
          const nextIdx = idx + 1 >= options.length ? 0 : idx + 1;
          return options[nextIdx].value;
        });
      }
    };

    if (menuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, optionsSet]); // Responsive queries

  const isMobile = useMediaQuery({
    maxWidth: breakpoints.mobile.maxWidth
  }); // open menu on button click

  const click = useClick(context, {
    enabled: !alwaysVisible
  }); // close menu on outside click or escape key

  const dismiss = useDismiss(context, {
    enabled: !alwaysVisible && !isMobile
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([click, dismiss, listNavigation]);
  return /*#__PURE__*/_jsx(SelectContext.Provider, {
    value: {
      isMobile,
      alwaysVisible,
      menuOpen,
      value,
      valueOption,
      selectedValueIndex,
      selectedValue,
      onMenuOpenChange: handleMenuOpenChange,
      onValueChange: handleValueChange,
      onValueSubmit: handleValueSubmit,
      onOptionAdd: handleOptionAdd,
      onOptionRemove: handleOptionRemove,
      onClose: closeMenu,
      // floating
      listRef,
      setReference: refs.setReference,
      setFloating: refs.setFloating,
      floatingStyles: alwaysVisible ? {} : floatingStyles,
      getReferenceProps,
      getFloatingProps,
      getItemProps
    },
    children: /*#__PURE__*/_jsx("div", {
      className: styles.megaSelect,
      children: children
    })
  });
}; // Button to open the select menu


const OpenButton = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    placeholder = 'Select an option',
    disabled = false,
    className
  } = _ref2;
  const {
    menuOpen,
    setReference,
    valueOption,
    getReferenceProps
  } = useContext(SelectContext);
  const composedRef = useComposedRefs(forwardedRef, setReference);
  return /*#__PURE__*/_jsxs("button", _objectSpread(_objectSpread({
    type: "button",
    ref: composedRef,
    disabled: disabled,
    className: clsx(styles.option, styles.openButton, {
      [styles['openButton--noValue']]: !valueOption || disabled,
      [styles['openButton--active']]: menuOpen
    }, className)
  }, getReferenceProps()), {}, {
    children: [valueOption !== null && valueOption !== void 0 && valueOption.image ? /*#__PURE__*/_jsx(OptionImage, {
      size: "medium",
      image: valueOption === null || valueOption === void 0 ? void 0 : valueOption.image,
      alt: valueOption === null || valueOption === void 0 ? void 0 : valueOption.label
    }) : null, /*#__PURE__*/_jsx(OptionContent, {
      label: (valueOption === null || valueOption === void 0 ? void 0 : valueOption.label) || placeholder,
      description: valueOption === null || valueOption === void 0 ? void 0 : valueOption.description
    }), /*#__PURE__*/_jsx(ArrowDownIcon, {
      className: styles.arrowIcon
    })]
  }));
});
OpenButton.displayName = 'Select.OpenButton';
const Option = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    value,
    label,
    description,
    icon,
    image,
    activeImage,
    selected,
    disabled,
    className,
    imageClassName
  } = _ref3;
  const {
    selectedValue,
    selectedValueIndex,
    onValueChange,
    onOptionAdd,
    onOptionRemove,
    getItemProps
  } = useContext(SelectContext);
  const {
    ref,
    index
  } = useListItem();
  const composedRef = useComposedRefs(forwardedRef, ref);
  const option = useMemo(() => ({
    value,
    label,
    description,
    image,
    activeImage,
    selected,
    disabled
  }), [value, label, description, image, activeImage, selected, disabled]); // add option to context on mount
  // to handle default selected value

  useLayoutEffect(() => {
    onOptionAdd(option);
    return () => onOptionRemove(option);
  }, [option, onOptionAdd, onOptionRemove]);
  const isSelected = selectedValue === value;
  return /*#__PURE__*/_jsxs("button", _objectSpread(_objectSpread({
    className: clsx(styles.option, {
      [styles['option--selected']]: isSelected && !disabled,
      [styles['option--disabled']]: disabled,
      [styles['option--hasBadge']]: Boolean(icon)
    }, className),
    disabled: disabled,
    tabIndex: selectedValueIndex === index ? 0 : -1,
    ref: composedRef
  }, getItemProps({
    onClick: () => onValueChange(value)
  })), {}, {
    children: [/*#__PURE__*/_jsx(OptionImage, {
      size: "big",
      isSelected: isSelected,
      image: image,
      activeImage: activeImage,
      alt: label,
      className: imageClassName
    }), /*#__PURE__*/_jsx(OptionContent, {
      label: label,
      description: description
    }), icon ? /*#__PURE__*/_jsx(OptionBadge, {
      icon: icon
    }) : null]
  }));
});
Option.displayName = 'Select.Option';

const OptionContent = _ref4 => {
  let {
    label,
    description
  } = _ref4;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.optionContent,
    children: [/*#__PURE__*/_jsx("strong", {
      className: styles.label,
      children: label
    }), description ? /*#__PURE__*/_jsx("span", {
      className: styles.description,
      children: description
    }) : null]
  });
};

const OptionBadge = _ref5 => {
  let {
    icon
  } = _ref5;
  return /*#__PURE__*/_jsx("span", {
    className: styles.badge,
    children: icon
  });
};

const OptionImage = _ref6 => {
  let {
    isSelected,
    activeImage,
    image,
    alt,
    size = 'medium',
    className
  } = _ref6;
  const optionImage = (isSelected ? activeImage : image) || image;
  const hasImage = Boolean(optionImage);
  if (!hasImage) return null;

  if (typeof optionImage === 'string') {
    return /*#__PURE__*/_jsx("span", {
      className: clsx(styles.image, styles[`image--${size}`], className),
      children: /*#__PURE__*/_jsx("img", {
        src: optionImage,
        alt: alt
      })
    });
  }

  return /*#__PURE__*/_jsx("span", {
    className: clsx(styles.image, styles[`image--${size}`], className),
    children: optionImage
  });
}; // the select menu can be rendered either
// inline or as a floating element.


const Content = /*#__PURE__*/forwardRef((_ref7, forwardedRef) => {
  let {
    children,
    variant = 'elevated',
    className
  } = _ref7;
  const {
    menuOpen,
    isMobile,
    alwaysVisible,
    setFloating,
    floatingStyles,
    getFloatingProps,
    listRef,
    onMenuOpenChange
  } = useContext(SelectContext);
  const composedRef = useComposedRefs(forwardedRef, setFloating); // remove elevation styles on mobile

  const contentVariant = isMobile ? 'inline' : variant;

  const content = /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread(_objectSpread({
    ref: composedRef,
    className: clsx(styles.content, {
      [styles['content--hidden']]: !menuOpen && !alwaysVisible,
      [styles[`content--${contentVariant}`]]: contentVariant
    }, className) // Apply floating styles and props only on desktop

  }, !isMobile ? {
    style: floatingStyles
  } : {}), getFloatingProps()), {}, {
    children: children
  })); // setting the fragment in `useLayoutEffect` to prevent
  // the maximum depth exceeded error in React
  // by caching the fragment in a state


  const {
    0: fragment,
    1: setFragment
  } = useState();
  useLayoutEffect(() => {
    setFragment(new DocumentFragment());
  }, []); // always render the content if it's always visible

  if (alwaysVisible) return content; // fix the children options not being mounted
  // when the menu is closed by rendering them in a virtual dom

  if (!menuOpen) {
    const frag = fragment;
    return frag ? /*#__PURE__*/createPortal( /*#__PURE__*/_jsx("div", {
      children: children
    }), frag) : null;
  } // render the menu as a Drawer on mobile


  if (isMobile) {
    return /*#__PURE__*/_jsx(Drawer, {
      open: menuOpen,
      onOpenChange: onMenuOpenChange,
      children: /*#__PURE__*/_jsx(DrawerContent, {
        children: /*#__PURE__*/_jsx(FloatingList, {
          elementsRef: listRef,
          children: content
        })
      })
    });
  }

  return (
    /*#__PURE__*/
    // ensure the floating element can appear outside any
    // potential parent containers that cause clipping
    _jsx(FloatingPortal, {
      children: /*#__PURE__*/_jsx(FloatingList, {
        elementsRef: listRef,
        children: content
      })
    })
  );
});
Content.displayName = 'Select.Content';
const Heading = /*#__PURE__*/forwardRef((_ref8, forwardedRef) => {
  let {
    title,
    subtitle,
    className
  } = _ref8;
  return /*#__PURE__*/_jsxs("div", {
    ref: forwardedRef,
    className: clsx(styles.heading, className),
    children: [title && /*#__PURE__*/_jsx("h4", {
      className: styles.title,
      children: title
    }), subtitle && /*#__PURE__*/_jsx("p", {
      className: styles.subtitle,
      children: subtitle
    })]
  });
});
Heading.displayName = 'Select.Heading';
const Group = /*#__PURE__*/forwardRef((_ref9, forwardedRef) => {
  let {
    className,
    children
  } = _ref9;
  return /*#__PURE__*/_jsx("div", {
    ref: forwardedRef,
    className: clsx(styles.group, className),
    children: children
  });
});
Group.displayName = 'Select.Group';

const Footer = _ref10 => {
  let {
    cancelText = 'Cancel',
    selectText = 'Select'
  } = _ref10;
  const {
    onValueSubmit,
    onClose
  } = useContext(SelectContext);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.footer,
    children: [/*#__PURE__*/_jsx(Button, {
      type: "button",
      variant: "link",
      className: styles.cancelButton,
      onClick: () => onClose(),
      children: cancelText
    }), /*#__PURE__*/_jsx(Button, {
      type: "button",
      onClick: () => onValueSubmit(),
      children: selectText
    })]
  });
};

export { MegaSelect as Root, OpenButton, Option, Content, Heading, Group, Footer };
export default MegaSelect;